import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { unlockAudio } from "../lib/audio";

type PublicRoom = {
  code: string;
  phase: string;
  playerCount: number;
  hostNickname: string;
};

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rooms, setRooms] = useState<PublicRoom[]>([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    let stop = false;
    async function load() {
      try {
        const res = await api<{ rooms: PublicRoom[] }>("/api/rooms");
        if (!stop) setRooms(res.rooms);
      } catch {
        /* ignore */
      }
    }
    void load();
    const id = window.setInterval(() => void load(), 4000);
    return () => {
      stop = true;
      window.clearInterval(id);
    };
  }, []);

  const waiting = rooms.filter((r) => r.phase === "lobby");
  const live = rooms.filter((r) => r.phase !== "lobby");

  async function join(code: string) {
    setErr("");
    const stored = (() => {
      try {
        const raw = sessionStorage.getItem("themix_player") || localStorage.getItem("themix_player");
        return raw ? (JSON.parse(raw) as { playerId?: string; code?: string }) : null;
      } catch {
        return null;
      }
    })();
    if (stored?.playerId && stored.code?.toUpperCase() === code.toUpperCase()) {
      await unlockAudio();
      nav(`/j/${code}`);
      return;
    }
    const nickname = user?.nickname?.trim() ?? "";
    if (nickname.length < 2) {
      nav(`/join?code=${encodeURIComponent(code)}`);
      return;
    }
    await unlockAudio();
    try {
      const res = await api<{ playerId: string; token: string }>(`/api/lobbies/${code}/join`, {
        method: "POST",
        body: JSON.stringify({ nickname }),
      });
      const blob = JSON.stringify({ ...res, reconnectToken: res.token, code });
      sessionStorage.setItem("themix_player", blob);
      localStorage.setItem("themix_player", blob);
      nav(`/j/${code}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Impossible de rejoindre");
    }
  }

  return (
    <section className="pt-8 md:pt-12 grid lg:grid-cols-[minmax(14rem,18rem)_minmax(0,1fr)_minmax(14rem,18rem)] gap-6 items-start">
      <aside className="rounded-3xl border border-mint/35 bg-ink-2 p-5 shadow-lg order-2 lg:order-1">
        <p className="uppercase tracking-[0.18em] text-mint text-xs">Lobby créé</p>
        {waiting.length === 0 ? (
          <p className="mt-3 opacity-70 text-sm">Aucun salon en attente.</p>
        ) : (
          <ul className="mt-4 grid gap-3">
            {waiting.map((r) => (
              <li key={r.code} className="grid gap-2">
                <p className="font-display text-2xl">{r.code}</p>
                <p className="text-sm opacity-70">
                  {r.hostNickname} · {r.playerCount} joueur{r.playerCount > 1 ? "s" : ""}
                </p>
                <button
                  type="button"
                  className="rounded-2xl bg-mint text-ink py-2 font-bold"
                  onClick={() => void join(r.code)}
                >
                  Rejoindre
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div className="grid gap-8 order-1 lg:order-2">
        <div className="max-w-xl">
          <p className="uppercase tracking-[0.2em] text-gold text-xs">Soirée musicale</p>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] mt-3">Le mot caché dans la chanson.</h1>
          <p className="mt-4 text-lg text-cream/80">
            Un thème s’affiche, un extrait démarre. Trouve le prénom, la couleur, la ville ou l’idée qui relie le morceau au
            thème ou à l’artiste.
          </p>
          {user ? (
            <p className="mt-3 text-mint">Bonjour {user.username}.</p>
          ) : (
            <p className="mt-3 text-cream/60 text-sm">
              Même compte que MusicGuess. La connexion est facultative —{" "}
              <Link className="text-gold" to="/compte">
                créer un compte
              </Link>
              .
            </p>
          )}
          {err && <p className="mt-2 text-pink text-sm">{err}</p>}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 max-w-lg">
          <Link
            to="/solo"
            className="rounded-3xl bg-gold text-ink font-semibold text-xl py-6 px-6 text-center shadow-lg"
          >
            Jouer en solo
          </Link>
          <Link
            to="/multi"
            className="rounded-3xl bg-pink text-cream font-semibold text-xl py-6 px-6 text-center shadow-lg"
          >
            Créer un salon
          </Link>
          <Link to="/join" className="sm:col-span-2 rounded-3xl border border-cream/20 py-4 text-center">
            Rejoindre avec un code
          </Link>
        </div>
        <aside className="rounded-3xl border border-gold/35 bg-ink-2 p-5 max-w-lg">
          <p className="uppercase tracking-[0.18em] text-gold text-xs">À retenir</p>
          <p className="mt-3 leading-snug">
            Toutes les réponses doivent être écrites en français et en toutes lettres (même les chiffres)
          </p>
        </aside>
      </div>

      <aside className="rounded-3xl border border-pink/40 bg-ink-2 p-5 shadow-lg order-3">
        <p className="uppercase tracking-[0.18em] text-pink text-xs">Partie en cours</p>
        {live.length === 0 ? (
          <p className="mt-3 opacity-70 text-sm">Aucune partie en cours.</p>
        ) : (
          <ul className="mt-4 grid gap-3">
            {live.map((r) => (
              <li key={r.code} className="grid gap-2">
                <p className="font-display text-2xl">{r.code}</p>
                <p className="text-sm opacity-70">
                  {r.hostNickname} · {r.playerCount} joueur{r.playerCount > 1 ? "s" : ""}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    className="rounded-2xl bg-mint text-ink py-2 font-bold"
                    onClick={() => void join(r.code)}
                  >
                    Rejoindre
                  </button>
                  <button
                    type="button"
                    className="rounded-2xl bg-pink text-cream py-2 font-bold"
                    onClick={() => {
                      void unlockAudio();
                      nav(`/j/${r.code}?spectate=1`);
                    }}
                  >
                    Spectateur
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </aside>
    </section>
  );
}
